import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Empty, Field, LiveDot } from "../../components/sc/ui";
import { TablePager, useClientPage } from "../../components/sc/Pager";

const INK = "#14101F";
const VIOLET = "#4931E8";
const MINT = "#1FBF75";

// The live board splits active intents by how long they have been waiting.
// Each lane is tinted so a merchant can see at a glance what is about to expire.
// Rows inside a lane stay white so the payer details keep maximum contrast.
const LANES = [
  {
    key: "fresh", label: "Just came in", hint: "under 2 min", upTo: 2,
    lane: "#FBFAF7", border: "#E3DDCD", accent: VIOLET, sub: "#6F6880", bar: VIOLET, pill: "#ECE8FB",
  },
  {
    key: "ageing", label: "Waiting", hint: "2–4 min", upTo: 4,
    lane: "#FFF4E3", border: "#E8B978", accent: "#9A5600", sub: "#7F6A4C", bar: "#E08A1E", pill: "#FFE7C4",
  },
  {
    key: "urgent", label: "About to expire", hint: "over 4 min", upTo: Infinity,
    lane: "#FDECE9", border: "#EDA093", accent: "#A81E16", sub: "#8A5B54", bar: "#D2453A", pill: "#FBD9D3",
  },
];

function inr(n) {
  if (n == null) return "₹0";
  if (n >= 100000) return `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 2)}L`;
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function inr2(n) {
  if (n == null) return "₹—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function payerName(o) {
  const n = (o.customer_name || "").trim();
  if (n) return n;
  const v = (o.payer_vpa || "").trim();
  if (v && v !== "unknown@upi") return v;
  return "—";
}

function payerSub(o) {
  const phone = (o.customer_phone || "").trim();
  if (phone) return phone;
  const v = (o.payer_vpa || "").trim();
  if (v && v !== "unknown@upi" && !(o.customer_name || "").trim()) return "";
  if (v && v !== "unknown@upi") return v;
  return "";
}

// The merchant's OWN order reference (the `note` they set when creating the
// payment, e.g. "MB2608…"). Falls back to the short code, then a dash.
function orderRef(o) {
  const n = (o.note || "").trim();
  if (n) return n;
  const c = (o.short_code || "").trim();
  return c ? `#${c}` : "—";
}

const ts = (v) => {
  const t = Date.parse(v || "");
  return Number.isFinite(t) ? t : null;
};

function mmss(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function Deposits() {
  // ---- settled-history filters (server-side) ----
  const [filter, setFilter] = useState("all"); // status
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amtMin, setAmtMin] = useState("");
  const [amtMax, setAmtMax] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [showHistory, setShowHistory] = useState(true);

  // ---- archived filters (client-side, mirrors settled history) ----
  const [arcSearch, setArcSearch] = useState("");
  const [arcStatus, setArcStatus] = useState("all");
  const [arcFrom, setArcFrom] = useState("");
  const [arcTo, setArcTo] = useState("");
  const [arcMin, setArcMin] = useState("");
  const [arcMax, setArcMax] = useState("");
  const [showArcFilters, setShowArcFilters] = useState(false);
  const [showArchived, setShowArchived] = useState(false); // collapsed by default

  const [txns, setTxns] = useState([]);
  const [stats, setStats] = useState(null);
  const [awaiting, setAwaiting] = useState([]);
  const [confirming, setConfirming] = useState("");
  const [expanded, setExpanded] = useState("");
  const [armedId, setArmedId] = useState("");
  const [archivedList, setArchivedList] = useState([]);
  const [awaitSearch, setAwaitSearch] = useState(""); // client-side search over the live board
  const [now, setNow] = useState(() => Date.now());

  const historyParams = useCallback(() => {
    const p = new URLSearchParams();
    if (filter !== "all") p.set("status", filter);
    if (search.trim()) p.set("search", search.trim());
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (amtMin) p.set("amount_min", amtMin);
    if (amtMax) p.set("amount_max", amtMax);
    return p;
  }, [filter, search, dateFrom, dateTo, amtMin, amtMax]);

  const loadTxns = useCallback(async () => {
    const p = historyParams();
    p.set("page", String(page));
    p.set("page_size", String(pageSize));
    const { data } = await api.get(`/transactions?${p.toString()}`);
    setTxns(data.items || []);
    setTotal(data.total || 0);
  }, [historyParams, page, pageSize]);

  const loadSide = useCallback(async () => {
    // limit=200 (the server cap) so a busy queue is never silently truncated.
    try { setAwaiting((await api.get("/intents?status=awaiting_payment&limit=200")).data); } catch { /* ignore */ }
    try { setArchivedList((await api.get("/intents?archived=true&limit=200")).data); } catch { /* ignore */ }
  }, []);

  // reset to page 1 whenever a history filter changes
  useEffect(() => { setPage(1); }, [filter, search, dateFrom, dateTo, amtMin, amtMax]);
  // (debounced) load whenever the query or page changes
  useEffect(() => {
    const t = setTimeout(() => { loadTxns().catch(() => {}); }, 300);
    return () => clearTimeout(t);
  }, [loadTxns]);

  useEffect(() => {
    api.get("/overview/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadSide();
    const t = setInterval(loadSide, 6000);
    return () => clearInterval(t);
  }, [loadSide]);

  // Second-resolution clock: lanes are derived from elapsed time, so a card has
  // to move between columns (and its countdown tick) without waiting for a poll.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!armedId) return;
    const t = setTimeout(() => setArmedId(""), 4000);
    return () => clearTimeout(t);
  }, [armedId]);

  const tapSettle = (i) => { if (armedId === i.id) confirmIntent(i.id); else setArmedId(i.id); };

  const confirmIntent = async (id) => {
    setConfirming(id);
    try {
      await api.post(`/intents/${id}/confirm`, {});
      toast.success("Payment marked as received");
      setArmedId("");
      await Promise.all([loadTxns(), loadSide()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setConfirming("");
    }
  };

  const archiveIntent = async (id) => {
    setArmedId("");
    try { await api.post(`/intents/${id}/archive`, {}); await loadSide(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to archive"); }
  };
  const unarchiveIntent = async (id) => {
    setArmedId("");
    try { await api.post(`/intents/${id}/unarchive`, {}); await loadSide(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to restore"); }
  };

  const exportCsv = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const qs = historyParams().toString();
      const res = await fetch(`${base}/api/transactions/export.csv${qs ? `?${qs}` : ""}`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a2 = document.createElement("a");
      a2.href = url; a2.download = "supercash_transactions.csv"; a2.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported ✦");
    } catch {
      toast.error("Export failed");
    }
  };

  const clearFilters = () => { setSearch(""); setDateFrom(""); setDateTo(""); setAmtMin(""); setAmtMax(""); setFilter("all"); };
  const hasFilters = search || dateFrom || dateTo || amtMin || amtMax || filter !== "all";

  const clearArcFilters = () => { setArcSearch(""); setArcFrom(""); setArcTo(""); setArcMin(""); setArcMax(""); setArcStatus("all"); };
  const hasArcFilters = arcSearch || arcFrom || arcTo || arcMin || arcMax || arcStatus !== "all";

  // ---- live board: bucket the active queue by how long it has been waiting ----
  const board = useMemo(() => {
    const q = awaitSearch.trim().toLowerCase();
    const buckets = { fresh: [], ageing: [], urgent: [] };
    for (const i of awaiting) {
      if (q && ![i.customer_phone, i.customer_name, i.short_code, i.note, i.amount]
        .some((f) => String(f ?? "").toLowerCase().includes(q))) continue;
      // Only live intents belong on the board — an intent past its window is on
      // its way to `expired` and is no longer settleable from here.
      const exp = ts(i.expires_at);
      if (exp !== null && exp <= now) continue;
      const created = ts(i.created_at) ?? now;
      const mins = (now - created) / 60000;
      buckets[mins < 2 ? "fresh" : mins < 4 ? "ageing" : "urgent"].push(i);
    }
    // Oldest first inside a lane: the most urgent card sits at the top.
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) => (ts(a.created_at) ?? 0) - (ts(b.created_at) ?? 0));
    }
    return buckets;
  }, [awaiting, awaitSearch, now]);

  const liveCount = board.fresh.length + board.ageing.length + board.urgent.length;

  // ---- archived: same filter vocabulary as settled history, applied client-side ----
  const archivedShown = useMemo(() => {
    const q = arcSearch.trim().toLowerCase();
    return archivedList
      .filter((i) => {
        if (q && ![i.customer_phone, i.customer_name, i.short_code, i.note, i.amount]
          .some((f) => String(f ?? "").toLowerCase().includes(q))) return false;
        if (arcStatus !== "all" && i.status !== arcStatus) return false;
        const day = String(i.created_at || "").slice(0, 10);
        if (arcFrom && day && day < arcFrom) return false;
        if (arcTo && day && day > arcTo) return false;
        const amt = Number(i.amount ?? 0);
        if (arcMin !== "" && amt < Number(arcMin)) return false;
        if (arcMax !== "" && amt > Number(arcMax)) return false;
        return true;
      })
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  }, [archivedList, arcSearch, arcStatus, arcFrom, arcTo, arcMin, arcMax]);
  // Archived intents arrive as one capped list (/intents?archived=true&limit=200), so page them here.
  const { slice: archivedPage, pager: archivedPager } = useClientPage(
    archivedShown, 10, [arcSearch, arcStatus, arcFrom, arcTo, arcMin, arcMax].join("|"));

  const fmtWhen = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="ui-page dep-page" data-testid="deposits-page">
      <div className="ui-grid ui-grid-3 dep-stats">
        {[
          { l: "Settled today", v: inr(stats?.today?.sum || 0), id: "deposit-settled-today" },
          { l: "This month", v: inr(stats?.month?.sum || 0), id: "deposit-month" },
          { l: "Awaiting now", v: String(liveCount), id: "deposit-pending" },
        ].map((s, idx) => (
          <div key={s.l} className={`ui-card ui-stat ${idx === 2 && liveCount > 0 ? "is-accent" : ""}`} data-testid={s.id}>
            <span className="ui-eyebrow">{s.l}</span>
            <span className="ui-stat-value dep-stat-value">{s.v}</span>
          </div>
        ))}
      </div>

      {/* LIVE BOARD — active intents, laned by how close they are to expiring. */}
      <section className="ui-card" data-testid="awaiting-card">
        <header className="ui-card-head">
          <div style={{ minWidth: 0 }}>
            <div className="ui-row" style={{ gap: 10 }}>
              <h3 className="ui-card-title">Incoming payments</h3>
              <LiveDot on label={`${liveCount} LIVE`} />
            </div>
            <p className="ui-card-sub">
              Match the <b>payer&apos;s phone number</b> to a card, then <b>Mark received</b> (tap twice to confirm).
              Cards move right as they age — the red lane is about to expire. Not ready? <b>Archive</b> it to settle later.
            </p>
          </div>
          <input data-testid="awaiting-search" className="ui-input dep-await-search" value={awaitSearch} onChange={(e) => setAwaitSearch(e.target.value)}
            placeholder="Search phone, name or order ref…" />
        </header>

        <div className="ui-card-pad">
          <div className="dep-board">
            {LANES.map((lane) => {
              const items = board[lane.key];
              return (
                <section key={lane.key} data-testid={`lane-${lane.key}`} className="dep-lane"
                  style={{ background: lane.lane, borderColor: lane.border }}>
                  <header className="dep-lane-head">
                    <span className="dep-lane-dot" style={{ background: lane.bar }} />
                    <span className="dep-lane-title">{lane.label}</span>
                    <span data-testid={`lane-${lane.key}-count`} className="dep-lane-count" style={{ background: lane.pill }}>{items.length}</span>
                    <span className="ui-mono dep-lane-hint" style={{ color: lane.accent }}>{lane.hint}</span>
                  </header>
                  <div className="sc-scroll dep-lane-body">
                    {items.length === 0 && (
                      <div className="ui-mono dep-lane-empty" style={{ color: lane.sub }}>
                        {awaitSearch ? "No matches" : "Nothing here"}
                      </div>
                    )}
                    {items.map((i) => (
                      <IntentCard key={i.id} i={i} lane={lane} now={now}
                        armed={armedId === i.id} busy={confirming === i.id}
                        onTap={tapSettle} onArchive={archiveIntent} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </section>

      {/* SETTLED HISTORY — searchable, filterable, paginated ledger. */}
      <section className="ui-card" data-testid="deposits-table">
        <SectionHeader title="Settled history" count={total} open={showHistory}
          onToggle={() => setShowHistory((v) => !v)} testId="history-toggle" />

        {showHistory && (
          <>
            <div className="dep-toolbar-wrap">
              <div className="ui-toolbar">
                <input data-testid="deposits-search" className="ui-input ui-grow" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order ref / phone / name / amount…" />
                <select data-testid="deposits-status" className="ui-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">All statuses</option>
                  <option value="settled">Auto-settled</option>
                  <option value="manually_confirmed">Manually confirmed</option>
                </select>
                <button data-testid="deposits-filters-toggle" type="button" className={`ui-btn ${hasFilters ? "dep-btn-active" : ""}`} aria-expanded={showFilters} onClick={() => setShowFilters((v) => !v)}>⛃ Filters{showFilters ? " ▲" : " ▾"}</button>
                <button data-testid="deposits-export" type="button" className="ui-btn" onClick={exportCsv}>↓ Export CSV</button>
              </div>

              {showFilters && (
                <div className="dep-filters">
                  <Field label="From">
                    <input data-testid="deposits-date-from" className="ui-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </Field>
                  <Field label="To">
                    <input data-testid="deposits-date-to" className="ui-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </Field>
                  <Field label="Min ₹">
                    <input data-testid="deposits-amt-min" className="ui-input" type="number" min="0" value={amtMin} onChange={(e) => setAmtMin(e.target.value)} placeholder="0" />
                  </Field>
                  <Field label="Max ₹">
                    <input data-testid="deposits-amt-max" className="ui-input" type="number" min="0" value={amtMax} onChange={(e) => setAmtMax(e.target.value)} placeholder="∞" />
                  </Field>
                  {hasFilters && <button data-testid="deposits-clear" type="button" className="ui-btn ui-btn-danger dep-clear" onClick={clearFilters}>Clear</button>}
                </div>
              )}
            </div>

            {txns.length === 0 ? (
              <Empty icon="🧾" title={hasFilters ? "No matches" : "No transactions yet"}>
                {hasFilters ? "No transactions match these filters." : "No transactions yet."}
              </Empty>
            ) : (
              <div className="ui-table-wrap sc-scroll">
                <table className="ui-table dep-table">
                  <thead>
                    <tr>
                      <th style={{ width: "26%" }}>Payer</th>
                      <th className="is-num">Amount</th>
                      <th>Order ref</th>
                      <th>Source</th>
                      <th>Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t) => {
                      const hasNotif = !!t.matched_notification;
                      const open = expanded === t.id;
                      return (
                        <Fragment key={t.id}>
                          <tr data-testid={`txn-row-${t.id}`} onClick={hasNotif ? () => setExpanded(open ? "" : t.id) : undefined}
                            className={`${hasNotif ? "is-click" : ""} ${open ? "is-active dep-row-open" : ""}`} aria-expanded={hasNotif ? open : undefined}>
                            <td data-label="Payer" className="dep-payer">
                              <span className="dep-payer-inner">
                                {hasNotif && <span className={`dep-caret ${open ? "is-open" : ""}`} aria-hidden="true">▸</span>}
                                <span style={{ minWidth: 0 }}>
                                  <span className="ui-cell-main ui-trunc dep-display">{payerName(t)}</span>
                                  {payerSub(t) && <span className="ui-cell-sub">{payerSub(t)}</span>}
                                </span>
                              </span>
                            </td>
                            <td data-label="Amount" className="is-num"><span className="ui-cell-main dep-display">{inr2(t.amount)}</span></td>
                            <td data-label="Order ref"><span title={orderRef(t)} className="ui-mono ui-muted ui-trunc" style={{ fontSize: 11.5 }}>{orderRef(t)}</span></td>
                            <td data-label="Source">
                              <span className="ui-badge" style={{ color: "#14101F", borderColor: INK, background: t.confirmation_source === "manual" ? "#FFF3D6" : "#E3F7EC" }}>
                                {t.confirmation_source === "manual" ? "manual" : "auto"}
                              </span>
                            </td>
                            <td data-label="Time"><span className="ui-mono ui-muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtWhen(t.created_at)}</span></td>
                            <td data-label="Status"><span className={`sc-status sc-status-${t.status}`}>{t.status}</span></td>
                          </tr>
                          {open && (
                            <tr className="dep-detail-row">
                              <td colSpan={6} data-label="">
                                <MatchedNotification n={t.matched_notification} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination (testids deposits-prev / deposits-next kept) */}
            <TablePager testId="deposits" page={page} total={total} size={pageSize} onPage={setPage} onSize={setPageSize} />
          </>
        )}
      </section>

      {/* ARCHIVED — parked deposits, still fully settleable. Collapsed by default. */}
      <section className="ui-card" data-testid="archived-card">
        <SectionHeader title="Archived" count={archivedList.length} open={showArchived}
          onToggle={() => setShowArchived((v) => !v)} testId="archived-toggle" />

        {showArchived && (
          <div className="ui-card-pad">
            <p className="ui-card-sub" style={{ margin: "0 0 12px" }}>
              Deposits you parked. Settle any of them when the payment lands, or restore it to the live board.
            </p>

            <div className="ui-toolbar">
              <input data-testid="archived-search" className="ui-input ui-grow" value={arcSearch} onChange={(e) => setArcSearch(e.target.value)} placeholder="Search order ref / phone / name / amount…" />
              <select data-testid="archived-status" className="ui-select" value={arcStatus} onChange={(e) => setArcStatus(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="awaiting_payment">Awaiting</option>
                <option value="expired">Expired</option>
              </select>
              <button data-testid="archived-filters-toggle" type="button" className={`ui-btn ${hasArcFilters ? "dep-btn-active" : ""}`} aria-expanded={showArcFilters} onClick={() => setShowArcFilters((v) => !v)}>⛃ Filters{showArcFilters ? " ▲" : " ▾"}</button>
            </div>

            {showArcFilters && (
              <div className="dep-filters">
                <Field label="From">
                  <input data-testid="archived-date-from" className="ui-input" type="date" value={arcFrom} onChange={(e) => setArcFrom(e.target.value)} />
                </Field>
                <Field label="To">
                  <input data-testid="archived-date-to" className="ui-input" type="date" value={arcTo} onChange={(e) => setArcTo(e.target.value)} />
                </Field>
                <Field label="Min ₹">
                  <input data-testid="archived-amt-min" className="ui-input" type="number" min="0" value={arcMin} onChange={(e) => setArcMin(e.target.value)} placeholder="0" />
                </Field>
                <Field label="Max ₹">
                  <input data-testid="archived-amt-max" className="ui-input" type="number" min="0" value={arcMax} onChange={(e) => setArcMax(e.target.value)} placeholder="∞" />
                </Field>
                {hasArcFilters && <button data-testid="archived-clear" type="button" className="ui-btn ui-btn-danger dep-clear" onClick={clearArcFilters}>Clear</button>}
              </div>
            )}

            <div className="dep-archived-list">
              {archivedShown.length === 0 && (
                <Empty icon="📦" title={archivedList.length === 0 ? "Nothing archived yet" : "No matches"}>
                  {archivedList.length === 0 ? "Nothing archived yet." : "No archived deposits match these filters."}
                </Empty>
              )}
              {archivedPage.map((i) => (
                <ArchivedRow key={i.id} i={i} armed={armedId === i.id} busy={confirming === i.id}
                  onTap={tapSettle} onRestore={unarchiveIntent} />
              ))}
            </div>
            <TablePager testId="archived-pager" {...archivedPager} />
          </div>
        )}
      </section>

      <style>{`
        .dep-page .dep-stat-value { font-size: 28px; }
        .dep-page .dep-display { font-family: "Inter", sans-serif; }
        .dep-page .dep-await-search { width: 300px; max-width: 100%; }
        .dep-page .dep-btn-active { color: ${VIOLET}; border-color: ${VIOLET}; box-shadow: 3px 3px 0 ${VIOLET}; }

        /* live board */
        .dep-page .dep-board { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; align-items: start; }
        .dep-page .dep-lane { border: 2px solid; border-radius: 14px; padding: 12px; min-width: 0; }
        .dep-page .dep-lane-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
        .dep-page .dep-lane-dot { width: 10px; height: 10px; border-radius: 100px; border: 1.5px solid ${INK}; flex: none; }
        .dep-page .dep-lane-title { font-family: "Inter", sans-serif; font-weight: 800; font-size: 14px; color: ${INK}; }
        .dep-page .dep-lane-count { font-family: "JetBrains Mono", monospace; font-size: 10.5px; font-weight: 700; padding: 1px 8px; border-radius: 100px; border: 2px solid ${INK}; color: ${INK}; }
        .dep-page .dep-lane-hint { font-size: 10.5px; font-weight: 700; margin-left: auto; }
        .dep-page .dep-lane-body { display: flex; flex-direction: column; gap: 10px; max-height: 620px; overflow-y: auto; padding: 2px 4px 4px 2px; }
        .dep-page .dep-lane-empty { font-size: 11.5px; padding: 18px 4px; text-align: center; border: 2px dashed currentColor; border-radius: 10px; opacity: .7; }

        .dep-page .dep-intent { background: #fff; border: 2px solid; border-radius: 12px; padding: 12px 13px; min-width: 0; transition: border-color .12s, box-shadow .12s; }
        .dep-page .dep-phone { font-family: "JetBrains Mono", monospace; font-weight: 700; font-size: 18px; letter-spacing: .02em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dep-page .dep-amount { font-family: "Inter", sans-serif; font-weight: 800; font-size: 20px; color: ${INK}; letter-spacing: -0.01em; }
        .dep-page .dep-name { font-size: 13px; font-weight: 600; color: #3d3650; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dep-page .dep-bar { height: 6px; border-radius: 100px; background: #EEE9DD; margin-top: 4px; overflow: hidden; border: 1px solid #E3DDCD; }
        .dep-page .dep-actions { display: flex; gap: 8px; margin-top: 11px; }
        .dep-page .dep-actions .ui-btn { min-height: 38px; font-size: 12.5px; }
        .dep-page .dep-armed { background: ${MINT} !important; color: ${INK} !important; }

        /* history */
        .dep-page .dep-toolbar-wrap { padding: 14px 20px; border-bottom: 2px solid var(--ui-line); display: flex; flex-direction: column; gap: 12px; }
        .dep-page .dep-filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; align-items: end; padding: 12px; background: #FBFAF7; border: 2px solid #ECE7DB; border-radius: 12px; margin-top: 2px; }
        .dep-page .ui-card-pad .dep-filters { margin-top: 12px; }
        .dep-page .dep-filters .ui-input { min-width: 0; font-family: "JetBrains Mono", monospace; font-size: 12.5px; }
        .dep-page .dep-clear { justify-self: start; }
        .dep-page .dep-table td.dep-payer { max-width: 260px; }
        .dep-page .dep-payer-inner { display: flex; align-items: flex-start; gap: 7px; min-width: 0; }
        .dep-page .dep-caret { display: inline-block; color: var(--ui-faint); font-size: 11px; margin-top: 3px; transition: transform .15s; flex: none; }
        .dep-page .dep-caret.is-open { transform: rotate(90deg); color: ${VIOLET}; }
        .dep-page .dep-table tr.dep-row-open td { border-bottom: none; }
        .dep-page .dep-table tr.dep-detail-row td { background: var(--ui-hover); max-width: none; padding: 4px 14px 16px 34px; }
        .dep-page .dep-page-btn { min-height: 36px; }

        /* archived */
        .dep-page .dep-archived-list { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
        .dep-page .dep-arc { display: flex; justify-content: space-between; align-items: center; gap: 14px; padding: 13px 15px; border: 2px solid; border-radius: 12px; flex-wrap: wrap; transition: border-color .12s, background .12s; min-width: 0; }
        .dep-page .dep-arc-meta { margin-top: 5px; display: flex; align-items: center; gap: 6px 12px; flex-wrap: wrap; min-width: 0; }
        .dep-page .dep-arc-actions { display: flex; align-items: center; gap: 8px; }

        /* section header */
        .dep-page .dep-section-head { all: unset; box-sizing: border-box; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 56px; padding: 14px 20px; }
        .dep-page .dep-section-head:hover { background: var(--ui-hover); border-radius: 16px; }
        .dep-page .dep-section-head:focus-visible { outline: 3px solid ${VIOLET}; outline-offset: -3px; border-radius: 16px; }
        .dep-page section.ui-card > .dep-section-head[aria-expanded="true"] { border-bottom: 2px solid var(--ui-line); border-radius: 16px 16px 0 0; }

        @media (max-width: 1080px) {
          .dep-page .dep-board { grid-template-columns: minmax(0, 1fr); }
          .dep-page .dep-lane-body { max-height: 440px; }
        }
        @media (max-width: 900px) {
          .dep-page .dep-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .dep-page .dep-stat-value { font-size: 22px; }
        }
        @media (max-width: 720px) {
          .dep-page .dep-await-search { width: 100%; }
          .dep-page .dep-table td.dep-payer { max-width: none; }
          .dep-page .dep-table td.dep-payer .dep-payer-inner { justify-content: flex-end; text-align: right; }
          .dep-page .dep-table tr.dep-detail-row { padding: 0 0 12px; }
          .dep-page .dep-table tr.dep-detail-row td { display: block; text-align: left; padding: 0 14px 4px; background: transparent; }
          .dep-page .dep-table tr.dep-row-open { border-bottom: none; background: var(--ui-hover); }
        }
        @media (max-width: 600px) {
          .dep-page .dep-stats { grid-template-columns: minmax(0, 1fr); gap: 10px; }
          .dep-page .dep-stats .ui-stat { flex-direction: row; align-items: center; justify-content: space-between; }
          .dep-page .dep-toolbar-wrap { padding: 12px 15px; }
          .dep-page .dep-section-head { padding: 12px 15px; }
          .dep-page .ui-toolbar > .ui-select, .dep-page .ui-toolbar > .ui-btn { flex: 1 1 auto; }
          .dep-page .dep-arc { padding: 12px; }
          .dep-page .dep-arc-actions { width: 100%; }
          .dep-page .dep-arc-actions .ui-btn { flex: 1 1 0; }
          .dep-page .dep-lane { padding: 10px; }
        }
      `}</style>
    </div>
  );
}

/** Collapsible panel header — shared by Settled history and Archived. */
function SectionHeader({ title, count, open, onToggle, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onToggle} aria-expanded={open} className="dep-section-head">
      <span className="ui-row" style={{ gap: 8, minWidth: 0 }}>
        <span className="ui-card-title">{title}</span>
        <span className="ui-mono ui-faint" style={{ fontSize: 12 }}>{count}</span>
      </span>
      <span className="ui-mono" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: VIOLET }}>
        {open ? "Hide" : "Show"}
        <span aria-hidden="true" style={{ display: "inline-block", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>▾</span>
      </span>
    </button>
  );
}

/** One live intent on the board. Vertical so it reads inside a narrow lane. */
function IntentCard({ i, lane, now, armed, busy, onTap, onArchive }) {
  const created = ts(i.created_at);
  const expires = ts(i.expires_at);
  const left = expires !== null ? expires - now : null;
  const windowMs = created !== null && expires !== null ? expires - created : null;
  const pct = windowMs && left !== null ? Math.max(0, Math.min(100, (left / windowMs) * 100)) : null;

  return (
    <div data-testid={`awaiting-${i.id}`} className="dep-intent"
      style={{ borderColor: armed ? MINT : lane.border, boxShadow: armed ? `0 0 0 3px #EFFBF4` : "none" }}>
      {/* Primary match target: the payer's phone number. */}
      <div className="dep-phone" style={{ color: i.customer_phone ? INK : "#b3261e" }}>
        {i.customer_phone || "no phone"}
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4, minWidth: 0 }}>
        <span className="dep-amount" style={{ flex: "none" }}>{inr2(i.amount)}</span>
        {i.customer_name && <span className="dep-name">{i.customer_name}</span>}
      </div>

      <div title={orderRef(i)} className="ui-mono ui-trunc" style={{ fontSize: 11, color: lane.sub, marginTop: 3 }}>
        {orderRef(i)}
      </div>

      {/* Time left — the reason this card is in this lane. */}
      {left !== null && (
        <div style={{ marginTop: 10 }}>
          <div className="ui-mono" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10.5, fontWeight: 700 }}>
            <span style={{ color: lane.sub, letterSpacing: ".05em" }}>TIME LEFT</span>
            <span data-testid={`left-${i.id}`} style={{ color: lane.accent, fontSize: 12.5 }}>{mmss(left)}</span>
          </div>
          {pct !== null && (
            <div className="dep-bar">
              <div style={{ width: `${pct}%`, height: "100%", background: lane.bar, transition: "width 1s linear" }} />
            </div>
          )}
        </div>
      )}

      <div className="dep-actions">
        <button data-testid={`secondary-${i.id}`} type="button" onClick={() => onArchive(i.id)} disabled={busy}
          className="ui-btn">
          Archive
        </button>
        <button data-testid={`confirm-${i.id}`} type="button" onClick={() => onTap(i)} disabled={busy}
          className={`ui-btn ${armed ? "dep-armed" : "ui-btn-primary"}`} style={{ flex: 1, minWidth: 0, fontWeight: 800 }}>
          {busy ? "Settling…" : armed ? "✓ Tap again" : "Mark received"}
        </button>
      </div>
    </div>
  );
}

/** One archived (parked) deposit — a full-width row, since the panel is full-width. */
function ArchivedRow({ i, armed, busy, onTap, onRestore }) {
  const expired = i.status === "expired";
  return (
    <div data-testid={`awaiting-${i.id}`} className="dep-arc"
      style={{ borderColor: armed ? MINT : "#ECE7DB", background: armed ? "#EFFBF4" : "#FBFAF7" }}>
      <div style={{ minWidth: 0, flex: "1 1 260px" }}>
        <div className="ui-row" style={{ gap: 9 }}>
          <span className="dep-phone" style={{ fontSize: 19, color: i.customer_phone ? INK : "#b3261e", minWidth: 0 }}>
            {i.customer_phone || "no phone"}
          </span>
          <span className="ui-badge" style={{ borderColor: INK, background: expired ? "#F1EEF6" : "#ECE8FB", color: expired ? "#5b5470" : VIOLET }}>
            {expired ? "expired" : "awaiting"}
          </span>
        </div>
        <div className="dep-arc-meta">
          <span className="dep-amount" style={{ fontSize: 17 }}>{inr2(i.amount)}</span>
          {i.customer_name && <span className="dep-name">{i.customer_name}</span>}
          <span className="ui-mono ui-faint ui-trunc" style={{ fontSize: 11, maxWidth: "100%" }} title={orderRef(i)}>{orderRef(i)}</span>
          <span className="ui-mono ui-muted" style={{ fontSize: 11 }}>
            {i.created_at ? new Date(i.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      </div>
      <div className="dep-arc-actions">
        <button data-testid={`secondary-${i.id}`} type="button" onClick={() => onRestore(i.id)} disabled={busy} className="ui-btn">
          Restore
        </button>
        <button data-testid={`confirm-${i.id}`} type="button" onClick={() => onTap(i)} disabled={busy} className={`ui-btn ${armed ? "dep-armed" : "ui-btn-primary"}`}
          style={{ fontWeight: 800, minWidth: armed ? 176 : 134 }}>
          {busy ? "Settling…" : armed ? "✓ Tap again to settle" : "Mark received"}
        </button>
      </div>
    </div>
  );
}

function MatchedNotification({ n }) {
  if (!n) return null;
  const when = n.captured_at ? new Date(n.captured_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : null;
  const items = [
    ["Amount", n.amount_paisa != null ? inr2(n.amount_paisa / 100) : null],
    ["Payer VPA", n.payer_vpa && n.payer_vpa !== "unknown@upi" ? n.payer_vpa : null],
    ["UPI ref", n.rrn],
    ["App", n.package_name],
    ["Captured", when],
    ["Source", n.on_device_parsed ? "Parsed on device" : "Parsed on server"],
  ].filter(([, v]) => v);
  return (
    <div data-testid="matched-notification" style={{ border: `2.5px solid ${INK}`, borderRadius: 14, background: "#fff", padding: 14, boxShadow: `3px 3px 0 ${INK}`, maxWidth: 640, textAlign: "left", minWidth: 0 }}>
      <div className="ui-row" style={{ gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ width: 28, height: 28, borderRadius: 8, background: "#DFF7E7", border: `2px solid ${INK}`, display: "grid", placeItems: "center", fontSize: 13, flex: "none" }}>🔔</span>
        <span className="ui-card-title" style={{ fontSize: 14 }}>Matched notification</span>
        {n.bank_label && <span className="ui-badge" style={{ marginLeft: "auto", borderColor: INK, background: "#ECE8FB", color: VIOLET }}>{n.bank_label}</span>}
      </div>
      {(n.title || n.body) ? (
        <div style={{ background: "#F5F2EC", border: "2px solid #ECE7DB", borderRadius: 10, padding: "10px 12px", marginBottom: 12, overflowWrap: "anywhere" }}>
          {n.title && <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{n.title}</div>}
          {n.body && <div style={{ fontSize: 12.5, color: "#51495f", lineHeight: 1.45 }}>{n.body}</div>}
        </div>
      ) : (
        <div className="ui-muted" style={{ fontSize: 12, fontStyle: "italic", marginBottom: 12 }}>
          Parsed on-device (notification text stays private on the phone).
        </div>
      )}
      <div className="ui-kv" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {items.map(([label, value]) => (
          <div key={label} className="ui-kv-item">
            <span className="ui-eyebrow" style={{ fontSize: 10 }}>{label}</span>
            <span className="ui-kv-val" style={{ fontSize: 12 }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
